import { Immutable } from "immer";
import React, { useEffect, useState } from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { Link } from "../../../components/Link";
import { FinancingCardType } from "../../../constants";
import { IMonths, WebPageURL } from "../../../models/nominals";
import {
    FinancingPlanMeta,
    getDefaultFinancingPlan,
} from "../../../utils/financing";
import { formatAPR, formatTermLength } from "../../../utils/format";
import { usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
import { FinancingModalTrigger } from "../../financing/FinancingModalTrigger";
import { FinancingCardImage } from "../FinancingCardImage";

import styles from "./Configurator.module.scss";

type Props = Immutable<{
    planLength?: IMonths | null;
    financingLink?: WebPageURL;
}>;

const getCardTypeByAppSlug = (
    appSlugName: string | null,
): FinancingCardType | null => {
    switch (appSlugName) {
        case "tempurpedic":
        case "sale2": {
            return FinancingCardType.TEMPUR_CC;
        }
        case "stearns": {
            return FinancingCardType.STEARNS_CC;
        }
        case "sealy": {
            return FinancingCardType.SEALY_CC;
        }
        case "cocoon": {
            return FinancingCardType.COCOON_CC;
        }
    }
    return null;
};

export const FinancingModalTriggerConfigurator = (props: Props) => {
    const appSlug = usePageSetting("app_slug");
    const cardType = getCardTypeByAppSlug(appSlug);
    const [defaultPlan, setDefaultPlan] = useState<FinancingPlanMeta | null>(
        null,
    );
    const financeLinkURL = usePageURL("finance_link");
    const homeURL = usePageURL("home");

    useEffect(() => {
        setDefaultPlan(getDefaultFinancingPlan());
    }, []);

    const financeLink = props.financingLink
        ? props.financingLink
        : financeLinkURL || homeURL;
    return (
        <div className={styles.root}>
            <div className={styles.cardImageHolder}>
                {cardType && <FinancingCardImage cardType={cardType} />}
            </div>
            {!props.planLength || !defaultPlan ? (
                richT(
                    t`<Trigger>Check if you Pre-Qualify</Trigger> <P>without affecting your credit score.</P>`,
                    {
                        Trigger: (children) => (
                            <FinancingModalTrigger modalType="prequal-app">
                                {children}
                            </FinancingModalTrigger>
                        ),
                        P: (children) => <p>{children}</p>,
                    },
                )
            ) : (
                <>
                    <Link className={styles.aprCopy} href={financeLink}>
                        <h6>
                            {richT(
                                interpolate(
                                    t`%(apr)s APR for %(length)s Months<sup>1</sup>`,
                                    {
                                        apr: defaultPlan
                                            ? formatAPR(defaultPlan.apr)
                                            : "",
                                        length: formatTermLength(
                                            props.planLength,
                                        ),
                                    },
                                ),
                            )}
                        </h6>
                    </Link>
                    <FinancingModalTrigger modalType="prequal-app">
                        {t`Check if you Pre-Qualify`}
                    </FinancingModalTrigger>
                </>
            )}
        </div>
    );
};
